import { useState } from "react";
import { CreditCard, User, Mail, Building2, Landmark, Banknote, AlertCircle, Phone, FileText, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import { SubmissionLayout, DownloadActions, StatusBadge } from "../../components/SubmissionLayout";
import BonsaiMate from "../../components/BonsaiMate";
import { Button } from "../../components/ui/button";
import { getUserRole, ROLES } from "../../config/roleConfig";
import { showsTransferFields } from "../../lib/paymentTypes";

function AlertBanner({ icon: Icon, color, children }) {
  const styles = {
    red: "bg-status-red-dim/80 border-red-200 text-status-red",
    yellow: "bg-yellow-50/80 border-yellow-200 text-yellow-800",
    amber: "bg-status-amber-dim/80 border-amber-200 text-status-amber",
  };
  return (
    <div className={`p-3 rounded-md border flex items-center gap-2.5 text-sm ${styles[color]}`} data-testid={`alert-${color}`}>
      <Icon className="w-4 h-4 shrink-0" />
      <span className="font-medium">{children}</span>
    </div>
  );
}

function HeroField({ icon: Icon, label, value, large = false }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-white/10/60 last:border-0">
      <div className="w-8 h-8 rounded-md bg-teal/8 flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-teal" />
      </div>
      <div className="min-w-0 flex-1">
        <span className="text-xs font-medium text-txt-faint uppercase tracking-wider block">{label}</span>
        <span className={`font-medium text-txt-faint block truncate ${large ? 'text-base' : 'text-sm'}`}>
          {value || "\u2014"}
        </span>
      </div>
    </div>
  );
}

function FinancialRow({ label, value, accent = false }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-white/10/40 last:border-0">
      <span className="text-sm text-txt-faint">{label}</span>
      <span className={`text-sm font-medium ${accent ? 'text-teal' : 'text-txt-faint'}`}>
        {value || "\u2014"}
      </span>
    </div>
  );
}

// ─── Brief 50 (13 Jul 2026): Payment Status badge + transition workflow ─────
//
// Renders the current ``payment_status`` (Pending / Approved / Sent /
// Complete / Cancelled) and, subject to role gating, the next-valid
// transition buttons. Uses the same POST-then-refetch loop already
// proven by the reassign-fe modal on the invoice tracker.

const _PAYMENT_STATUS_STYLES = {
  Pending:   { bg: "bg-slate-500/15",    text: "text-slate-300",       label: "Pending" },
  Approved:  { bg: "bg-blue-500/15",     text: "text-blue-300",        label: "Approved" },
  Sent:      { bg: "bg-status-amber/15", text: "text-status-amber",    label: "Sent" },
  Complete:  { bg: "bg-teal/15",         text: "text-teal",            label: "Complete" },
  Cancelled: { bg: "bg-status-red/15",   text: "text-status-red",      label: "Cancelled" },
};

function PaymentStatusBadge({ status }) {
  const s = _PAYMENT_STATUS_STYLES[status] || _PAYMENT_STATUS_STYLES.Pending;
  return (
    <span
      data-testid="payment-status-badge"
      data-payment-status={status || "Pending"}
      className={`px-2.5 py-1 rounded-full text-xs font-medium border ${s.bg} ${s.text} border-white/10`}
    >
      {s.label}
    </span>
  );
}

// ─── Item 24 (Brief 50 follow-up, 15 Jul 2026): PDF Status row ─────────────
//
// Tracks the payment-request PDF's own lifecycle — separate from
// payment_status above. Reuses the exact _PAYMENT_STATUS_STYLES badge
// pattern (border + bg/text pair) per V's "template IS spec" direction;
// no new chrome invented. Defensive: any value outside the known enum
// (including missing/undefined, which predates this field) falls back
// to the "not_generated" grey style rather than rendering blank.

const _PDF_STATUS_STYLES = {
  not_generated: { bg: "bg-slate-500/15",    text: "text-slate-300",    label: "Not Generated" },
  generated:     { bg: "bg-status-amber/15", text: "text-status-amber", label: "Generated" },
  filed:         { bg: "bg-teal/15",         text: "text-teal",         label: "Filed" },
  failed:        { bg: "bg-status-red/15",   text: "text-status-red",   label: "Failed" },
};

function PdfStatusBadge({ status }) {
  const s = _PDF_STATUS_STYLES[status] || _PDF_STATUS_STYLES.not_generated;
  return (
    <span
      data-testid="pdf-status-badge"
      data-pdf-status={status || "not_generated"}
      className={`px-2.5 py-1 rounded-full text-xs font-medium border ${s.bg} ${s.text} border-white/10`}
    >
      {s.label}
    </span>
  );
}

function PdfStatusRow({ submission, formatDate }) {
  // Defensive read — records that predate this field (or any row that
  // never went through the PDF-gen/filing path) render cleanly as
  // "Not Generated" rather than blank/error.
  const pdfStatus = submission?.pdf_status || "not_generated";
  const filedAt = submission?.pdf_filed_at || "";
  const sharepointUrl = submission?.pdf_sharepoint_url || "";

  return (
    <div
      className="flex items-center justify-between bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm px-4 py-3"
      data-testid="pdf-status-row"
    >
      <div className="flex items-center gap-2 text-sm text-txt-faint">
        <FileText className="w-4 h-4 text-teal" />
        <span className="font-medium">PDF Status</span>
        <PdfStatusBadge status={pdfStatus} />
      </div>
      {filedAt && (
        <span className="text-xs text-txt-muted" data-testid="pdf-status-filed-at">
          {sharepointUrl ? (
            <a href={sharepointUrl} target="_blank" rel="noreferrer" className="text-teal underline">
              Filed {formatDate(filedAt)}
            </a>
          ) : (
            <>Filed {formatDate(filedAt)}</>
          )}
        </span>
      )}
    </div>
  );
}

// Transition matrix mirrors the backend state machine at
// ``routes_submissions._PAYMENT_TRANSITIONS``. Kept small and readable
// so the UI never lets the user pick an action the server would refuse.
function _next_transitions(current, isDirector, isCashier) {
  const s = current || "Pending";
  const out = [];
  // BM-H3516 (Vanessa, 3 Oct 2026): Accounts may approve too. The server
  // refuses anyone approving their own request.
  if (s === "Pending" && (isDirector || isCashier)) out.push({ to: "Approved", label: "Mark Approved", tone: "teal", requiresReason: false });
  if (s === "Approved" && (isDirector || isCashier)) out.push({ to: "Sent", label: "Mark Sent", tone: "amber", requiresReason: false });
  if (s === "Sent" && (isDirector || isCashier)) out.push({ to: "Complete", label: "Mark Complete", tone: "teal", requiresReason: false });
  if (s !== "Cancelled" && s !== "Complete" && isDirector) out.push({ to: "Cancelled", label: "Cancel", tone: "red", requiresReason: true });
  if (s === "Cancelled" && isDirector) out.push({ to: "Pending", label: "Reopen", tone: "teal", requiresReason: true });
  return out;
}

function PaymentStatusWorkflow({ submission, isDirector, isCashier, onChanged }) {
  const currentStatus = submission?.payment_status || "Pending";
  const transitions = _next_transitions(currentStatus, isDirector, isCashier);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState("");

  async function apply(next) {
    if (busy) return;
    setBusy(true);
    try {
      const headers = {
        "X-Bonsai-User-Id":
          sessionStorage.getItem("bonsai_user_id") ||
          localStorage.getItem("bonsai_user_id") || "",
      };
      await axios.patch(
        `${API}/submissions/${submission.id}/payment-status`,
        { new_status: next.to, reason: reason.trim() },
        { headers },
      );
      toast.success(`Payment marked ${next.to}`);
      setPending(null);
      setReason("");
      if (typeof onChanged === "function") await onChanged();
    } catch (err) {
      toast.error(errorMessage(err, "Transition failed"));
    } finally {
      setBusy(false);
    }
  }

  if (!submission?.id) return null;

  return (
    <div
      className="bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm p-4"
      data-testid="payment-status-workflow"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-txt-faint">
          <Clock className="w-4 h-4 text-teal" />
          <span className="font-medium">Status</span>
          <PaymentStatusBadge status={currentStatus} />
        </div>
        <div className="flex flex-wrap gap-2" data-testid="payment-status-transitions">
          {transitions.length === 0 && (
            <span className="text-xs text-txt-muted italic" data-testid="payment-status-no-transitions">
              No transitions available for your role.
            </span>
          )}
          {transitions.map((t) => (
            <Button
              key={t.to}
              size="sm"
              disabled={busy}
              data-testid={`payment-status-transition-${t.to}`}
              className={
                "text-xs " + (
                  t.tone === "red"   ? "bg-status-red/10 text-status-red hover:bg-status-red/20" :
                  t.tone === "amber" ? "bg-status-amber/10 text-status-amber hover:bg-status-amber/20" :
                                       "bg-teal/10 text-teal hover:bg-teal/20"
                )
              }
              onClick={() => t.requiresReason ? setPending(t) : apply(t)}
            >
              {t.label}
            </Button>
          ))}
        </div>
      </div>

      {pending && (
        <div className="mt-3 border-t border-white/10/60 pt-3" data-testid="payment-status-reason-block">
          <label className="text-xs text-txt-faint block mb-1">
            Reason ({pending.to === "Pending" ? "e.g. reopening" : "min 3 chars"})
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="payment-status-reason-input"
            placeholder={pending.to === "Cancelled" ? "Why is this being cancelled?" : "Reason"}
            className="w-full bg-white border border-teal/20 rounded px-3 py-2 text-sm text-txt-faint mb-2"
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setPending(null); setReason(""); }}
              className="text-xs text-txt-muted"
              data-testid="payment-status-reason-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy || reason.trim().length < 3}
              onClick={() => apply(pending)}
              className="text-xs bg-teal text-white hover:bg-teal-hover"
              data-testid="payment-status-reason-confirm"
            >
              Confirm {pending.to}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PaymentRequestDetail({ submission, formatDate: formatDateProp, handleDownloadPDF, handleExportJson, downloadingPdf, navigate, isDirector, onDelete, onSubmissionRefresh }) {
  // Defensive fallback — some callers of this component in tests don't
  // pass formatDate; avoid a hard crash on the new PDF Status row.
  const formatDate = typeof formatDateProp === "function" ? formatDateProp : (v) => v || "";
  // Resolve a field value by checking every location where it might live:
  //   1. submission.data.<key>           – normal flat form storage
  //   2. submission.<key>                – root-level (some docs store fields here)
  //   3. submission.data.<section>.<key> – nested export-json shape inside data
  //   4. submission.<section>.<key>      – nested export-json shape at root
  const raw  = submission?.data || {};
  const root = submission || {};
  // raw_form_data exists in the export-json format as a flat copy of all fields
  const rfd  = raw.raw_form_data || root.raw_form_data || {};
  const pick = (...paths) => {
    for (const p of paths) {
      if (p != null && p !== "") return p;
    }
    return "";
  };

  const pd   = raw.payment_details || root.payment_details || {};
  const ma   = raw.matter_account  || root.matter_account  || {};
  const pp   = raw.payee_payment   || root.payee_payment   || {};
  const ver  = raw.verification    || root.verification    || {};
  const appr = raw.approval        || root.approval        || {};

  const data = {
    ...rfd,
    ...raw,
    requester_name:          pick(raw.requester_name,          root.requester_name,          pd.requester_name,          rfd.requester_name),
    requester_email:         pick(raw.requester_email,         root.requester_email,         pd.requester_email,         rfd.requester_email),
    client_name:             pick(raw.client_name,             root.client_name,             root.primary_label, pd.client_name, rfd.client_name),
    matter_reference:        pick(raw.matter_reference,        root.matter_reference,        root.secondary_label, pd.matter_reference, rfd.matter_reference),
    account_type:            pick(raw.account_type,            root.account_type,            ma.account_type,            rfd.account_type),
    invoice_saved:           pick(raw.invoice_saved,           root.invoice_saved,           ma.invoice_saved,           rfd.invoice_saved),
    payee_name:              pick(raw.payee_name,              root.payee_name,              pp.payee_name,              rfd.payee_name),
    payee_reference:         pick(raw.payee_reference,         root.payee_reference,         pp.payee_reference,         rfd.payee_reference),
    payee_bank:              pick(raw.payee_bank,              root.payee_bank,              pp.payee_bank,              rfd.payee_bank),
    payee_account_number:    pick(raw.payee_account_number,    root.payee_account_number,    pp.payee_account_number,    rfd.payee_account_number),
    payee_sort_code:         pick(raw.payee_sort_code,         root.payee_sort_code,         pp.payee_sort_code,         rfd.payee_sort_code),
    payment_amount:          pick(raw.payment_amount,          root.payment_amount,          pp.payment_amount,          rfd.payment_amount),
    payment_description:     pick(raw.payment_description,     root.payment_description,     pp.payment_description,     rfd.payment_description),
    verbal_verification:     pick(raw.verbal_verification,     root.verbal_verification,     ver.verbal_verification,     rfd.verbal_verification),
    verification_date:       pick(raw.verification_date,       root.verification_date,       ver.verification_date,       rfd.verification_date),
    verification_contact:    pick(raw.verification_contact,    root.verification_contact,    ver.verification_contact,    rfd.verification_contact),
    no_verification_reason:  pick(raw.no_verification_reason,  root.no_verification_reason,  ver.no_verification_reason,  rfd.no_verification_reason),
    notification_preference: pick(raw.notification_preference, root.notification_preference, appr.notification_preference, rfd.notification_preference),
    is_urgent:               pick(raw.is_urgent,               root.is_urgent,               appr.is_urgent,               rfd.is_urgent),
    urgent_reason:           pick(raw.urgent_reason,           root.urgent_reason,           appr.urgent_reason,           rfd.urgent_reason),
    // Brief 49 (13 Jul 2026): payment_type + inter-account fields.
    // Frontend backfill: existing rows without payment_type display as
    // "Third-Party" per the acceptance criterion.
    payment_type:              pick(raw.payment_type,              root.payment_type,              rfd.payment_type,              "Third-Party"),
    source_account:            pick(raw.source_account,            root.source_account,            rfd.source_account),
    destination_account:       pick(raw.destination_account,       root.destination_account,       rfd.destination_account),
    internal_transfer_notes:   pick(raw.internal_transfer_notes,   root.internal_transfer_notes,   rfd.internal_transfer_notes),
  };

  const formatAmount = (amount) => {
    if (!amount) return "\u2014";
    const num = parseFloat(String(amount).replace(/[^0-9.]/g, ''));
    if (isNaN(num)) return amount;
    return `\u00A3${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const alerts = [];
  if (data.is_urgent === "Yes") alerts.push({ color: "red", icon: AlertCircle, text: `URGENT PAYMENT${data.urgent_reason ? ` \u2014 ${data.urgent_reason}` : ""}` });
  if (data.verbal_verification === "No") alerts.push({ color: "amber", icon: Phone, text: `Verbal verification NOT made${data.no_verification_reason ? ` \u2014 ${data.no_verification_reason}` : ""}` });
  if (data.invoice_saved === "No") alerts.push({ color: "yellow", icon: FileText, text: "Invoice NOT saved to Financials folder" });

  return (
    <>
    <SubmissionLayout
      navigate={navigate}
      title={data.matter_reference || "Payment Request"}
      subtitle={data.client_name}
      matterReference={data.matter_reference}
      badge="Payment Request"
      badgeColor="purple"
      badgeIcon={CreditCard}
      isTest={data.is_test_submission}
      isDirector={isDirector}
      onDelete={onDelete}
      actions={<DownloadActions handleDownloadPDF={handleDownloadPDF} handleExportJson={handleExportJson} downloadingPdf={downloadingPdf} />}
    >
      {/* Status Row */}
      <div className="flex items-center justify-between bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 px-5 py-3.5 shadow-sm" data-testid="payment-header-info">
        <div className="flex items-center gap-4">
          <StatusBadge status={submission.status} />
          {/* Brief 49: Payment Type badge in the header. Colour-coded
              by inter-account vs Third-Party. */}
          <span
            data-testid="payment-type-badge"
            data-payment-type={data.payment_type}
            className={
              "text-xs font-medium px-2 py-1 rounded-full border " +
              (showsTransferFields(data.payment_type, data)
                ? "bg-teal/10 text-teal border-teal/30"
                : "bg-purple-50 text-purple-700 border-purple-200")
            }
          >
            {data.payment_type}
          </span>
          <span className="text-sm text-txt-faint">Submitted {formatDate(submission.created_at)}</span>
        </div>
        <span className="text-xs text-txt-muted font-mono tracking-wide">{submission.id?.slice(0, 8)}</span>
      </div>

      {/* Alert Banners */}
      {alerts.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="payment-alerts">
          {alerts.map((a, i) => (
            <AlertBanner key={i} icon={a.icon} color={a.color}>{a.text}</AlertBanner>
          ))}
        </div>
      )}

      {/* Amount Hero */}
      <div className="bg-gradient-to-r from-purple-50 to-purple-100/80 rounded-md border-2 border-purple-200 p-6 text-center" data-testid="payment-amount-hero">
        <div className="text-3xl font-bold text-purple-700">{formatAmount(data.payment_amount)}</div>
        <div className="text-purple-600 mt-1">
          {showsTransferFields(data.payment_type, data)
            ? `${data.source_account || "\u2014"} \u2192 ${data.destination_account || "\u2014"}`
            : `to ${data.payee_name || "\u2014"}`}
        </div>
        <div className="text-sm text-purple-500 mt-2">
          {data.account_type === "Client Account" ? "From Client Account" : "From Office Account"}
        </div>
      </div>

      {/* Brief 50 (13 Jul 2026): payment_status workflow. Renders the
          current status badge plus the next-valid transition buttons
          gated by the caller's role. Sits between the amount hero and
          the bento grid so the tracker action is the first thing an
          accounts user sees after landing on the detail page. */}
      <PaymentStatusWorkflow
        submission={submission}
        isDirector={!!isDirector}
        isCashier={(() => {
          try {
            const email = (
              sessionStorage.getItem("bonsai_user_email") ||
              localStorage.getItem("bonsai_user_email") ||
              sessionStorage.getItem("bonsai_user_id") ||
              localStorage.getItem("bonsai_user_id") || ""
            ).toLowerCase();
            return getUserRole(email) === ROLES.ACCOUNTS;
          } catch (_err) {  // nosilent:brief50_localstorage_unavailable
            return false;
          }
        })()}
        onChanged={onSubmissionRefresh}
      />

      {/* Item 24 (Brief 50 follow-up, 15 Jul 2026): PDF Status row —
          surfaces the payment-request PDF's own lifecycle (generated /
          filed to SharePoint / failed), separate from the payment
          workflow status above. */}
      <PdfStatusRow submission={submission} formatDate={formatDate} />

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="payment-bento-grid">

        {/* Requester Info — 8 cols */}
        <div className="md:col-span-8 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-requester-info">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <User className="w-4 h-4" /> Payment Details
            </h2>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            <HeroField icon={User} label="Requester" value={data.requester_name} large />
            <HeroField icon={Mail} label="Email" value={data.requester_email} />
            <HeroField icon={Building2} label="Client Name" value={data.client_name} large />
            <HeroField icon={FileText} label="Matter Reference" value={data.matter_reference} large />
          </div>
        </div>

        {/* Account Type — 4 cols */}
        <div className="md:col-span-4 space-y-4">
          <div className="bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-account-type">
            <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
              <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
                <Banknote className="w-4 h-4" /> Account & Approval
              </h2>
            </div>
            <div className="p-5 space-y-3">
              <FinancialRow label="Account Type" value={data.account_type} accent />
              <FinancialRow label="Invoice Saved" value={data.invoice_saved} accent={data.invoice_saved === "No"} />
              <FinancialRow label="Notification" value={data.notification_preference === "Yes" ? "Notify when paid" : "No notification"} />
              <FinancialRow label="Urgent" value={data.is_urgent} accent={data.is_urgent === "Yes"} />
            </div>
          </div>

          {/* Submission Meta — 4 cols */}
          <div className="bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-submission-meta">
            <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
              <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
                <Clock className="w-4 h-4" /> Submission
              </h2>
            </div>
            <div className="p-5 space-y-3">
              <FinancialRow label="ID" value={submission.id?.slice(0, 8)} />
              <FinancialRow label="Created" value={formatDate(submission.created_at)} />
              <FinancialRow label="Status" value={submission.status} />
            </div>
          </div>
        </div>

        {/* Payee & Bank — 12 cols */}
        {/* Brief 49: conditional section — Third-Party keeps the
            pre-Brief-49 payee/bank grid; inter-account transfers
            (CTO / OTC / OTS) render source/destination/notes instead. */}
        {showsTransferFields(data.payment_type, data) ? (
          <div className="md:col-span-12 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-inter-account">
            <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
              <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
                <Landmark className="w-4 h-4" /> Inter-account transfer ({data.payment_type})
              </h2>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              <HeroField icon={Building2} label="Source account" value={data.source_account} large />
              <HeroField icon={Building2} label="Destination account" value={data.destination_account} large />
              <HeroField icon={Banknote} label="Amount" value={formatAmount(data.payment_amount)} large />
            </div>
            {data.internal_transfer_notes && (
              <div className="mx-5 mb-5 p-3.5 bg-[color:var(--surface-inset)] rounded-md text-sm text-txt-faint border border-white/10/60" data-testid="internal-transfer-notes-block">
                <div className="font-medium text-txt-faint mb-1 text-xs uppercase tracking-wider">Internal transfer notes</div>
                {data.internal_transfer_notes}
              </div>
            )}
          </div>
        ) : (
          <div className="md:col-span-12 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-payee-bank">
            <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
              <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
                <Landmark className="w-4 h-4" /> Payee & Bank Details
              </h2>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-x-8">
              <HeroField icon={User} label="Payee Name" value={data.payee_name} large />
              <HeroField icon={FileText} label="Payee Reference" value={data.payee_reference || "N/A"} />
              <HeroField icon={Landmark} label="Bank" value={data.payee_bank} />
              <HeroField icon={CreditCard} label="Account Number" value={data.payee_account_number} />
              <HeroField icon={CreditCard} label="Sort Code" value={data.payee_sort_code} />
              <HeroField icon={Banknote} label="Amount" value={formatAmount(data.payment_amount)} large />
            </div>
            {data.payment_description && (
              <div className="mx-5 mb-5 p-3.5 bg-[color:var(--surface-inset)] rounded-md text-sm text-txt-faint border border-white/10/60">
                <div className="font-medium text-txt-faint mb-1 text-xs uppercase tracking-wider">Description</div>
                {data.payment_description}
              </div>
            )}
          </div>
        )}
      </div>
    </SubmissionLayout>
    {data.matter_reference && (
      <BonsaiMate context="matter" matterReference={data.matter_reference} />
    )}
    </>
  );
}
